import React, { useState, useEffect, useCallback } from 'react';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { isPushSupported, getExistingPushSubscription, enablePushNotifications } from '../../utils/pushNotifications';
import { API_ENDPOINTS } from '../../config/api';
import SecretShopTab from '../Customer/SecretShopTab';
import { parseCustomerShippingAddress, buildProfileShippingPayload } from '../../utils/customerShippingAddress';

const G = '#c9a962'; // gold accent
const BG = '#0b0b0f';
const CARD = '#15151c';
const BORDER = 'rgba(201,169,98,0.25)';
const TEXT = '#f2ede2';
const TEXT_DIM = '#9a9488';
const MIN_PASSWORD_LENGTH = 8; // matches SECRET_SHOP_MIN_PASSWORD_LENGTH in the backend

const shell = {
  minHeight: '100vh',
  background: `radial-gradient(ellipse at top, #1a1a22 0%, ${BG} 60%)`,
  color: TEXT,
  fontFamily: "'Georgia', 'Times New Roman', serif",
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '48px 20px 80px',
};

const wordmark = {
  fontSize: 13,
  letterSpacing: '0.3em',
  textTransform: 'uppercase',
  color: G,
  marginBottom: 40,
  textAlign: 'center',
};

const card = {
  background: CARD,
  border: `1px solid ${BORDER}`,
  borderRadius: 4,
  padding: '36px 32px',
  width: '100%',
  maxWidth: 420,
  boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
};

const h1 = {
  fontSize: 24,
  fontWeight: 400,
  margin: '0 0 8px 0',
  letterSpacing: '0.02em',
};

const sub = {
  fontSize: 13,
  color: TEXT_DIM,
  margin: '0 0 28px 0',
  fontFamily: "'Helvetica Neue', Arial, sans-serif",
  lineHeight: 1.5,
};

const label = {
  fontSize: 11,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: TEXT_DIM,
  fontFamily: "'Helvetica Neue', Arial, sans-serif",
  display: 'block',
  marginBottom: 6,
};

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  background: '#0e0e13',
  border: `1px solid ${BORDER}`,
  borderRadius: 3,
  padding: '12px 14px',
  color: TEXT,
  fontSize: 15,
  fontFamily: "'Helvetica Neue', Arial, sans-serif",
  marginBottom: 18,
};

const button = {
  width: '100%',
  background: G,
  color: '#1a1408',
  border: 'none',
  borderRadius: 3,
  padding: '13px 18px',
  fontSize: 13,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  fontWeight: 600,
  fontFamily: "'Helvetica Neue', Arial, sans-serif",
  cursor: 'pointer',
};

const errorStyle = {
  color: '#e0a0a0',
  fontSize: 13,
  fontFamily: "'Helvetica Neue', Arial, sans-serif",
  marginBottom: 16,
};

const linkBtn = {
  background: 'transparent',
  border: 'none',
  color: G,
  fontSize: 13,
  fontFamily: "'Helvetica Neue', Arial, sans-serif",
  cursor: 'pointer',
  textDecoration: 'underline',
  padding: 0,
  marginTop: 18,
};

const JoinScreen = () => {
  const { signupWithCode } = useCustomerAuth();
  const [mode, setMode] = useState('join'); // 'join' | 'login'
  const [code, setCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!code.trim() || !firstName.trim() || !phone.trim() || !password) {
      setError('Enter your code, your name, your phone number and a password');
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await signupWithCode(code.trim(), firstName.trim(), phone.trim(), password);
    } catch (err) {
      setError(err.message || 'That code did not work.');
    } finally {
      setLoading(false);
    }
  };

  if (mode === 'login') {
    return <PhoneLoginScreen onBack={() => setMode('join')} />;
  }

  return (
    <div style={shell}>
      <div style={wordmark}>◆ Members Only ◆</div>
      <div style={card}>
        <h1 style={h1}>Request Access</h1>
        <p style={sub}>
          Enter the code you were given, your name and your phone number, and create a password — you'll use your
          phone and password to log back in. No email needed.
        </p>
        {error && <p style={errorStyle}>{error}</p>}
        <form onSubmit={handleSubmit}>
          <label style={label}>Access Code</label>
          <input
            style={{ ...inputStyle, textTransform: 'uppercase', letterSpacing: '0.1em' }}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoCapitalize="characters"
            autoCorrect="off"
            autoComplete="off"
          />
          <label style={label}>Your Name</label>
          <input style={inputStyle} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          <label style={label}>Phone</label>
          <input
            style={inputStyle}
            type="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="So we can reach you about pickups — also how you log back in"
          />
          <label style={label}>Create a Password</label>
          <input
            style={inputStyle}
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
          />
          <label style={label}>Confirm Password</label>
          <input
            style={inputStyle}
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          <button type="submit" style={button} disabled={loading}>
            {loading ? 'Entering…' : 'Enter'}
          </button>
        </form>
        <button type="button" style={linkBtn} onClick={() => setMode('login')}>
          Already a member? Log back in →
        </button>
      </div>
    </div>
  );
};

const PhoneLoginScreen = ({ onBack }) => {
  const { loginWithPhone } = useCustomerAuth();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!phone.trim() || !password) {
      setError('Enter the phone number you joined with and your password');
      return;
    }
    setLoading(true);
    try {
      await loginWithPhone(phone.trim(), password);
    } catch (err) {
      setError(err.message || 'Wrong phone number or password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={shell}>
      <div style={wordmark}>◆ Members Only ◆</div>
      <div style={card}>
        <h1 style={h1}>Log Back In</h1>
        <p style={sub}>
          Use the phone number you joined with and your password. Joined before passwords? Your password is your
          phone number — you'll be asked to create a new one once you're in.
        </p>
        {error && <p style={errorStyle}>{error}</p>}
        <form onSubmit={handleSubmit}>
          <label style={label}>Phone Number</label>
          <input style={inputStyle} type="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoFocus />
          <label style={label}>Password</label>
          <input
            style={inputStyle}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button type="submit" style={button} disabled={loading}>
            {loading ? 'Checking…' : 'Log In'}
          </button>
        </form>
        <button type="button" style={linkBtn} onClick={onBack}>
          ← Back to access code
        </button>
      </div>
    </div>
  );
};

const NotificationPrompt = () => {
  const { getAuthHeaders } = useCustomerAuth();
  const [status, setStatus] = useState('checking'); // 'checking' | 'unsupported' | 'off' | 'on' | 'enabling' | 'error'
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isPushSupported()) {
      setStatus('unsupported');
      return;
    }
    getExistingPushSubscription()
      .then((sub) => setStatus(sub ? 'on' : 'off'))
      .catch(() => setStatus('off'));
  }, []);

  const handleEnable = async () => {
    setStatus('enabling');
    setError('');
    try {
      await enablePushNotifications(getAuthHeaders());
      setStatus('on');
    } catch (err) {
      setError(err.message || 'Could not enable notifications.');
      setStatus('off');
    }
  };

  if (status === 'checking' || status === 'unsupported' || status === 'on') return null;

  return (
    <div
      style={{
        background: CARD,
        border: `1px solid ${BORDER}`,
        borderRadius: 4,
        padding: '14px 16px',
        marginBottom: 20,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        fontFamily: "'Helvetica Neue', Arial, sans-serif",
        flexWrap: 'wrap',
      }}
    >
      <div style={{ fontSize: 13, color: TEXT_DIM }}>
        Get notified when your order is delivered, and when &amp; where to pick up.
        {error && <div style={{ color: '#e0a0a0', marginTop: 4 }}>{error}</div>}
      </div>
      <button
        type="button"
        onClick={handleEnable}
        disabled={status === 'enabling'}
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: '#1a1408',
          background: G,
          border: 'none',
          borderRadius: 3,
          padding: '9px 16px',
          cursor: status === 'enabling' ? 'wait' : 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        {status === 'enabling' ? 'Enabling…' : '🔔 Enable Notifications'}
      </button>
    </div>
  );
};

const panelSection = {
  borderTop: `1px solid ${BORDER}`,
  paddingTop: 16,
  marginTop: 16,
};

const panelHeading = {
  fontSize: 12,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: G,
  marginBottom: 4,
};

const whyStyle = { ...sub, fontSize: 12, margin: '0 0 10px 0' };
const smallButton = { ...button, width: 'auto', padding: '9px 16px', fontSize: 12 };
const panelInput = { ...inputStyle, marginBottom: 10, fontSize: 14, padding: '10px 12px' };
const okStyle = { color: '#9fd0a0', fontSize: 12, margin: '0 0 8px 0' };
const panelError = { ...errorStyle, fontSize: 12, margin: '0 0 8px 0' };

const instagramUrl = (handle) => `https://instagram.com/${handle}`;

/** Pop-out under "Welcome <name>": drop-off address, phone (their login), Instagram and password. */
const guideList = { margin: '0 0 12px 0', paddingLeft: 18, fontSize: 12, color: TEXT_DIM, lineHeight: 1.6 };

const ProfilePanel = ({ hasPassword, onPasswordSet, instagram, onInstagramSaved, onRecheckInstagram }) => {
  const { customer, getAuthHeaders, refreshCustomer } = useCustomerAuth();
  const saved = parseCustomerShippingAddress(customer?.shippingAddress);

  const [address, setAddress] = useState({
    street: saved?.street || '',
    apartment: saved?.apartment || '',
    city: saved?.city || '',
    state: saved?.state || '',
    postalCode: saved?.postalCode || '',
  });
  const [addressStatus, setAddressStatus] = useState({ saving: false, ok: '', error: '' });

  const [phone, setPhone] = useState(customer?.phone || '');
  const [phoneStatus, setPhoneStatus] = useState({ saving: false, ok: '', error: '' });

  const [igHandle, setIgHandle] = useState(instagram.handle);
  const [igStatus, setIgStatus] = useState({ saving: false, ok: '', error: '' });
  const [checkingIg, setCheckingIg] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordStatus, setPasswordStatus] = useState({ saving: false, ok: '', error: '' });

  const putJson = async (url, body) => {
    const res = await fetch(url, {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Could not save');
    return data;
  };

  const saveAddress = async () => {
    if (!address.street.trim() || !address.city.trim() || !address.postalCode.trim()) {
      setAddressStatus({ saving: false, ok: '', error: 'Enter at least the street, suburb and postcode' });
      return;
    }
    setAddressStatus({ saving: true, ok: '', error: '' });
    try {
      await putJson(API_ENDPOINTS.customerProfile.me, {
        shippingAddress: buildProfileShippingPayload({ ...address, country: 'AU' }),
      });
      await refreshCustomer();
      setAddressStatus({ saving: false, ok: 'Address saved — it will be filled in for drop-off orders.', error: '' });
    } catch (err) {
      setAddressStatus({ saving: false, ok: '', error: err.message });
    }
  };

  const savePhone = async () => {
    if (!phone.trim()) {
      setPhoneStatus({ saving: false, ok: '', error: 'Enter a phone number' });
      return;
    }
    setPhoneStatus({ saving: true, ok: '', error: '' });
    try {
      await putJson(API_ENDPOINTS.secretShop.myPhone, { phone: phone.trim() });
      await refreshCustomer();
      setPhoneStatus({ saving: false, ok: 'Phone saved — use this number to log in from now on.', error: '' });
    } catch (err) {
      setPhoneStatus({ saving: false, ok: '', error: err.message });
    }
  };

  const saveInstagram = async () => {
    setIgStatus({ saving: true, ok: '', error: '' });
    try {
      const data = await putJson(API_ENDPOINTS.secretShop.myInstagram, { handle: igHandle.trim() });
      const saved = data?.data?.instagramHandle || '';
      setIgHandle(saved);
      onInstagramSaved(saved);
      setIgStatus({
        saving: false,
        ok: saved ? 'Saved — now comment on any of my posts to test it. You should get a DM saying you\'re connected.' : 'Instagram removed.',
        error: '',
      });
    } catch (err) {
      setIgStatus({ saving: false, ok: '', error: err.message });
    }
  };

  const savePassword = async () => {
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setPasswordStatus({ saving: false, ok: '', error: `Password must be at least ${MIN_PASSWORD_LENGTH} characters` });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordStatus({ saving: false, ok: '', error: "Passwords don't match" });
      return;
    }
    setPasswordStatus({ saving: true, ok: '', error: '' });
    try {
      await putJson(API_ENDPOINTS.secretShop.myPassword, {
        password: newPassword,
        ...(hasPassword ? { currentPassword } : {}),
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordStatus({ saving: false, ok: 'Password saved.', error: '' });
      onPasswordSet();
    } catch (err) {
      setPasswordStatus({ saving: false, ok: '', error: err.message });
    }
  };

  const setField = (field) => (e) => setAddress({ ...address, [field]: e.target.value });

  return (
    <div style={{ ...card, maxWidth: 'none', padding: '20px 22px', marginBottom: 20, fontFamily: "'Helvetica Neue', Arial, sans-serif" }}>
      <div style={panelHeading}>Drop-off address</div>
      <p style={whyStyle}>So we know where to bring your order when you choose drop-off. Only used for your deliveries.</p>
      {addressStatus.error && <p style={panelError}>{addressStatus.error}</p>}
      {addressStatus.ok && <p style={okStyle}>{addressStatus.ok}</p>}
      <input style={panelInput} placeholder="Street address" autoComplete="address-line1" value={address.street} onChange={setField('street')} />
      <input style={panelInput} placeholder="Unit / apartment (optional)" autoComplete="address-line2" value={address.apartment} onChange={setField('apartment')} />
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8 }}>
        <input style={panelInput} placeholder="Suburb" autoComplete="address-level2" value={address.city} onChange={setField('city')} />
        <input style={panelInput} placeholder="State" autoComplete="address-level1" value={address.state} onChange={setField('state')} />
        <input style={panelInput} placeholder="Postcode" autoComplete="postal-code" value={address.postalCode} onChange={setField('postalCode')} />
      </div>
      <button type="button" style={smallButton} onClick={saveAddress} disabled={addressStatus.saving}>
        {addressStatus.saving ? 'Saving…' : 'Save address'}
      </button>

      <div style={panelSection}>
        <div style={panelHeading}>Phone number</div>
        <p style={whyStyle}>
          It's how you log in, and how we can reach you about your orders.
        </p>
        {phoneStatus.error && <p style={panelError}>{phoneStatus.error}</p>}
        {phoneStatus.ok && <p style={okStyle}>{phoneStatus.ok}</p>}
        <input style={panelInput} type="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <button type="button" style={smallButton} onClick={savePhone} disabled={phoneStatus.saving}>
          {phoneStatus.saving ? 'Saving…' : 'Save phone'}
        </button>
      </div>

      <div style={panelSection}>
        <div style={panelHeading}>
          Instagram{' '}
          {instagram.handle && (
            <span style={{ color: instagram.linked ? '#9fd0a0' : TEXT_DIM, textTransform: 'none', letterSpacing: 0 }}>
              {instagram.linked ? '· linked ✅' : '· not tested yet'}
            </span>
          )}
        </div>
        <p style={whyStyle}>
          So when you comment on my posts or tag me in your story, I can DM you where I'll be so you can join me.
          Pickup needs your Instagram linked.
        </p>
        {!instagram.linked && (
          <ol style={guideList}>
            <li>
              Open Instagram, tap your profile picture (bottom right) — your username is at the top. Type it below
              and tap <strong style={{ color: TEXT }}>Save Instagram</strong>.
            </li>
            <li>
              Go to{' '}
              {instagram.businessInstagram ? (
                <a href={instagramUrl(instagram.businessInstagram)} target="_blank" rel="noopener noreferrer" style={{ color: G }}>
                  my Instagram
                </a>
              ) : (
                'my Instagram'
              )}{' '}
              and comment anything on any post — from the same account you saved.
            </li>
            <li>
              Within a minute I'll DM you "you're connected ✅". Not in your inbox? Check{' '}
              <strong style={{ color: TEXT }}>Message requests</strong>.
            </li>
            <li>
              Come back here and tap <strong style={{ color: TEXT }}>Check again</strong> — it'll say linked ✅.
            </li>
          </ol>
        )}
        {igStatus.error && <p style={panelError}>{igStatus.error}</p>}
        {igStatus.ok && <p style={okStyle}>{igStatus.ok}</p>}
        <input style={panelInput} placeholder="@yourusername" autoCapitalize="none" autoCorrect="off"
          value={igHandle} onChange={(e) => setIgHandle(e.target.value)} />
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" style={smallButton} onClick={saveInstagram} disabled={igStatus.saving}>
            {igStatus.saving ? 'Saving…' : 'Save Instagram'}
          </button>
          {instagram.handle && !instagram.linked && instagram.businessInstagram && (
            <a href={instagramUrl(instagram.businessInstagram)} target="_blank" rel="noopener noreferrer"
              style={{ color: G, fontSize: 12 }}>
              Comment on any of my posts →
            </a>
          )}
          {instagram.handle && !instagram.linked && (
            <button
              type="button"
              style={{ ...linkBtn, marginTop: 0, fontSize: 12 }}
              disabled={checkingIg}
              onClick={async () => {
                setCheckingIg(true);
                await onRecheckInstagram();
                setCheckingIg(false);
              }}
            >
              {checkingIg ? 'Checking…' : 'Check again'}
            </button>
          )}
        </div>
      </div>

      <div style={panelSection}>
        <div style={panelHeading}>{hasPassword ? 'Change password' : 'Create a password'}</div>
        <p style={whyStyle}>
          {hasPassword
            ? 'Keeps your account yours — nobody can get in with just your phone number.'
            : 'Right now your password is your phone number, which anyone who knows it could use. Set your own to keep your account yours.'}
        </p>
        {passwordStatus.error && <p style={panelError}>{passwordStatus.error}</p>}
        {passwordStatus.ok && <p style={okStyle}>{passwordStatus.ok}</p>}
        {hasPassword && (
          <input style={panelInput} type="password" autoComplete="current-password" placeholder="Current password"
            value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
        )}
        <input style={panelInput} type="password" autoComplete="new-password" placeholder={`New password (${MIN_PASSWORD_LENGTH}+ characters)`}
          value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
        <input style={panelInput} type="password" autoComplete="new-password" placeholder="Confirm new password"
          value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
        <button type="button" style={smallButton} onClick={savePassword} disabled={passwordStatus.saving}>
          {passwordStatus.saving ? 'Saving…' : 'Save password'}
        </button>
      </div>
    </div>
  );
};

const CatalogScreen = () => {
  const { customer, logout, getAuthHeaders } = useCustomerAuth();
  const [announcement, setAnnouncement] = useState('');
  const [hasPassword, setHasPassword] = useState(true);
  const [profileOpen, setProfileOpen] = useState(false);
  const [instagram, setInstagram] = useState({ handle: '', linked: false, businessInstagram: '', hasUpcomingPickup: false });

  const handleLogout = () => {
    const warning = customer?.phone
      ? "Log out? You'll be able to log back in with your phone number."
      : "Log out? You didn't give a phone number, so you'll need a brand new access code to get back in — you won't be able to track this order anymore.";
    if (window.confirm(warning)) logout();
  };

  const loadAnnouncement = useCallback(async () => {
    try {
      const res = await fetch(API_ENDPOINTS.secretShop.myAccess, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data?.success) {
        setAnnouncement(data.data.announcement || '');
        setHasPassword(!data.data.needsPassword);
        setInstagram({
          handle: data.data.instagramHandle || '',
          linked: !!data.data.instagramLinked,
          businessInstagram: data.data.businessInstagram || '',
          hasUpcomingPickup: !!data.data.hasUpcomingPickup,
        });
      }
    } catch {
      // Non-fatal.
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    loadAnnouncement();
  }, [loadAnnouncement]);

  return (
    <div style={shell}>
      <div style={{ width: '100%', maxWidth: 560 }}>
        <div style={wordmark}>◆ Members Only ◆</div>
        <h1 style={{ ...h1, textAlign: 'center', marginBottom: 24 }}>Exclusive Products</h1>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 24,
            fontFamily: "'Helvetica Neue', Arial, sans-serif",
          }}
        >
          <button
            type="button"
            onClick={() => setProfileOpen((open) => !open)}
            aria-expanded={profileOpen}
            style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
          >
            <div style={{ fontSize: 12, color: TEXT_DIM, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Welcome
            </div>
            <div style={{ fontSize: 18, color: TEXT }}>
              {customer?.firstName || 'Member'}{' '}
              <span style={{ fontSize: 12, color: G }}>{profileOpen ? '▴ Close profile' : '▾ My profile'}</span>
            </div>
          </button>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={logout}
              style={{
                fontSize: 12,
                color: TEXT_DIM,
                background: 'transparent',
                border: `1px solid ${BORDER}`,
                borderRadius: 3,
                padding: '8px 14px',
                cursor: 'pointer',
              }}
            >
              Log out
            </button>
          </div>
        </div>
        {profileOpen && (
          <ProfilePanel
            hasPassword={hasPassword}
            onPasswordSet={() => setHasPassword(true)}
            instagram={instagram}
            onRecheckInstagram={loadAnnouncement}
            onInstagramSaved={(handle) =>
              // A new handle has to be tested again; the same one keeps its linked state.
              setInstagram((ig) => ({ ...ig, handle, linked: ig.linked && handle.toLowerCase() === ig.handle.toLowerCase() }))
            }
          />
        )}
        {instagram.hasUpcomingPickup && instagram.businessInstagram && (
          <div
            style={{
              background: CARD,
              border: `1px solid ${BORDER}`,
              borderRadius: 4,
              padding: '14px 16px',
              marginBottom: 20,
              fontFamily: "'Helvetica Neue', Arial, sans-serif",
              fontSize: 13,
              color: TEXT,
              lineHeight: 1.5,
            }}
          >
            📸 Comment on any of my{' '}
            <a href={instagramUrl(instagram.businessInstagram)} target="_blank" rel="noopener noreferrer" style={{ color: G }}>
              Instagram posts
            </a>{' '}
            or tag me in your story, and I'll DM you where I'll be.
            {!instagram.handle && (
              <>
                {' '}
                <button type="button" onClick={() => setProfileOpen(true)}
                  style={{ ...linkBtn, marginTop: 0, fontSize: 13 }}>
                  Add your Instagram first
                </button>
              </>
            )}
          </div>
        )}
        {announcement && (
          <div
            style={{
              textAlign: 'center',
              background: CARD,
              border: `1px solid ${BORDER}`,
              borderRadius: 4,
              padding: '14px 18px',
              marginBottom: 20,
              color: TEXT,
              fontSize: 14,
              lineHeight: 1.5,
              fontFamily: "'Helvetica Neue', Arial, sans-serif",
              whiteSpace: 'pre-wrap',
            }}
          >
            {announcement}
          </div>
        )}
        <NotificationPrompt />
        <div
          style={{
            background: CARD,
            border: `1px solid ${BORDER}`,
            borderRadius: 4,
            padding: 20,
            fontFamily: "'Helvetica Neue', Arial, sans-serif",
          }}
        >
          <SecretShopTab showAccessTabHint={false} />
        </div>
      </div>
    </div>
  );
};

const ExclusiveApp = () => {
  const { isAuthenticated, isLoading } = useCustomerAuth();
  // Only gate on the *initial* token-restore check, not every later signup/login call —
  // otherwise JoinScreen unmounts (losing its own error state) on every failed attempt,
  // since the auth context flips isLoading true/false around every request it makes.
  const [hasHydrated, setHasHydrated] = useState(false);

  useEffect(() => {
    if (!isLoading) setHasHydrated(true);
  }, [isLoading]);

  if (!hasHydrated) {
    return <div style={shell} />;
  }

  return isAuthenticated ? <CatalogScreen /> : <JoinScreen />;
};

export default ExclusiveApp;
